// apps/web/src/main.tsx
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { useUIStore } from './stores/uiStore';
import App from './App';
import { preloadGuideBody, guideSlugFromPath } from './content/guides/bodies';
import './lib/i18n';
import './styles/globals.css';

// Initialize theme before render to prevent flash
useUIStore.getState().initializeTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

const container = document.getElementById('root')!;

const tree = (
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ToastProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>
);

async function bootstrap() {
  // Resolve a guide's body chunk BEFORE hydrating. Hydrating into a Suspense
  // boundary that is still loading makes React discard the prerendered article
  // and re-render it client-side (error #421).
  const slug = guideSlugFromPath(window.location.pathname);
  if (slug) {
    try {
      await preloadGuideBody(slug);
    } catch {
      // Chunk fetch failed — fall through and let Suspense handle it.
    }
  }

  // Routes built by scripts/prerender.mjs arrive with markup already in #root,
  // so hydrate them instead of throwing that HTML away. Routes that fall
  // through to the SPA shell have an empty container and mount normally.
  if (container.hasChildNodes()) {
    ReactDOM.hydrateRoot(container, tree);
  } else {
    ReactDOM.createRoot(container).render(tree);
  }
}

void bootstrap();
